import type { MessageKey } from '@reos/i18n';
import type { ContractDetailDto, ContractDto } from './contracts-api.js';
import type { SignatureSessionDto, SignatureSignerDto } from './signature-api.js';
import type { PartyInvitationRef } from './contract-presentation.js';

/**
 * U KOHO SMLOUVA JE — jedna funkce pro cely admin (krok SML1 bod 1).
 *
 * Predloha `docs/design/ux-v3/SmlouvyPrehled.dc.html` neradi smlouvy podle
 * technickeho stavu (`draft` / `awaiting_signature`), ale podle toho, U KOHO
 * leží: Aktivní · Na tobě · U klientů · Ověření podpisu · Podepsané. Tentyz
 * vypocet potrebuje:
 *
 *  - filtr a pocty nad prehledem `/smlouvy`,
 *  - ODZNAK „Smlouvy" v menu (= pocet „Na tobě", `components/layout.tsx`),
 *  - radek „Čeká na mě" na Dnesku (`routes/dnesek.tsx`),
 *  - obrazovka podpisu (stav kazde strany).
 *
 * Do tohoto kroku to byla tri ruzna pravidla: menu a Dnesek brali
 * `status=awaiting_signature` (tedy smlouvu, ktera ceka na KLIENTA, hlasily
 * jako „na tobě"), prehled filtroval podle holeho stavu a „Elektronické
 * podpisy" mely vlastni `PENDING_STATUSES`. Tri kopie teze otazky se vzdy
 * rozejdou — proto je odpoved tady, jednou, jako cista funkce bez Reactu
 * a bez site (testovatelna bez DOM).
 *
 * Vstupem je to, co uz admin stahuje: radek smlouvy (`/v1/contracts`),
 * podepisovani (`/v1/contract-signatures`), volitelne detail smlouvy (osnova
 * s pocty) a pozvanky klientu (`/v1/client-invitations`). Nic se nedohaduje:
 * kdyz data chybi, smlouva skonci tam, kde ji neni co vytykat — „na tobě".
 */

/** Filtry predlohy v jejim poradi. `aktivni` je SOUHRN, ne misto. */
export const CONTRACT_WHERE_FILTERS = [
  'aktivni',
  'na-tobe',
  'u-klientu',
  'overeni-podpisu',
  'podepsane',
] as const;
export type ContractWhereFilter = (typeof CONTRACT_WHERE_FILTERS)[number];

/** U koho smlouva skutecne je. */
export type ContractWhere = 'na-tobe' | 'u-klientu' | 'overeni-podpisu' | 'podepsane';

/**
 * JEDNA akce radku (predloha: sloupec „Další krok"). Vic tlacitek na radku
 * znamena, ze clovek musi rozhodovat, ktere z nich je to spravne.
 */
export type ContractNextAction = 'sign' | 'continue' | 'remind' | 'upload-scan' | 'download';

export const CONTRACT_FILTER_LABEL_KEY: Readonly<Record<ContractWhereFilter, MessageKey>> = {
  aktivni: 'webAdmin.contracts.where.filterActive',
  'na-tobe': 'webAdmin.contracts.where.filterOnYou',
  'u-klientu': 'webAdmin.contracts.where.filterClients',
  'overeni-podpisu': 'webAdmin.contracts.where.filterVerify',
  podepsane: 'webAdmin.contracts.where.filterSigned',
};

export const CONTRACT_ACTION_LABEL_KEY: Readonly<Record<ContractNextAction, MessageKey>> = {
  sign: 'webAdmin.contracts.where.actionSign',
  continue: 'webAdmin.contracts.where.actionContinue',
  remind: 'webAdmin.contracts.where.actionRemind',
  'upload-scan': 'webAdmin.contracts.where.actionUploadScan',
  download: 'webAdmin.contracts.where.actionDownload',
};

/**
 * Krok APP3 bod 1 (nalez A31) — STAV SMLOUVY V HLAVICCE: JEDEN PREVOD.
 *
 * Tentyz prevod stavu na popisek mely DVE kopie (`contracts.tsx` a
 * `routes/elektronicke-podpisy.tsx`). Je to jedna tabulka, takze zije tady,
 * u ostatnich odpovedi na „co se o smlouve rika".
 */
export const CONTRACT_STATUS_LABEL_KEY: Readonly<Record<ContractDto['status'], MessageKey>> = {
  draft: 'webAdmin.contracts.status.draft',
  ready_to_send: 'webAdmin.contracts.status.ready_to_send',
  awaiting_signature: 'webAdmin.contracts.status.awaiting_signature',
  signed: 'webAdmin.contracts.status.signed',
  archived: 'webAdmin.contracts.status.archived',
};

/**
 * Krok APP3 bod 1 (nalez A31) — KDYZ SI STAV ODPORUJE S OSNOVOU, REKNI TO.
 *
 * Demo smlouva Sojovice mela 1. 11. 2026 stav „Podepisuje se" a soucasne
 * devet nevyplnenych udaju. Stav je pravdivy (podepisovani skutecne bezi) a
 * pocet taky — rozpor je mezi NIMI, a dokud ho hlavicka nejmenuje, vypada to
 * jako vada pocitadla. Hlavicka proto vedle stavu rekne, kolik udaju ve
 * smlouve zbyva, prave kdyz stav tvrdi, ze uz se podepisuje nebo ze je
 * smlouva pripravena k odeslani.
 *
 * `missing` je cislo Z OSNOVY SERVERU (`fillPlan.missing`) — tedy tentyz
 * zdroj, z nehoz je „zbývá N" u tlacitka a „N chybí" v panelu kontrol.
 * `null` = osnovu jeste neznáme, pak se netvrdi nic.
 */
export function contractHeaderState(input: {
  readonly status: ContractDto['status'];
  readonly missing: number | null | undefined;
}): { readonly statusKey: MessageKey; readonly gap: number | null } {
  const statusKey = CONTRACT_STATUS_LABEL_KEY[input.status];
  const missing = input.missing ?? null;
  const contradicts = input.status === 'ready_to_send' || input.status === 'awaiting_signature';
  return { statusKey, gap: contradicts && missing !== null && missing > 0 ? missing : null };
}

export interface ContractStanding {
  readonly where: ContractWhere;
  /** `true` = patri do filtru „Aktivní" (jeste se s ni neco deje). */
  readonly active: boolean;
  /** Stav smlouvy lidsky — prvni radek sloupce „Stav". */
  readonly stateKey: MessageKey;
  readonly stateValues: Readonly<Record<string, string | number>>;
  /** Druhy radek: PROC / DO KDY. `null` = neni co dodat. */
  readonly whyKey: MessageKey | null;
  readonly whyValues: Readonly<Record<string, string | number>>;
  readonly action: ContractNextAction;
  /**
   * Vyplnenost smlouvy v procentech pro prouzek v radku; `null` kdyz admin
   * detail (osnovu) nema — prouzek se pak nekresli, zadna nula nastesti.
   */
  readonly percent: number | null;
}

export interface ContractStandingInput {
  readonly row: ContractDto;
  /** Detail s osnovou (`fillPlan`) — bez nej se nekresli „zbývá N". */
  readonly detail?: ContractDetailDto | null | undefined;
  /** Posledni podepisovani smlouvy; `null` = jeste se nepodepisuje. */
  readonly session?: SignatureSessionDto | null | undefined;
}

/** Cesta, ktera se dokoncuje NA PAPIRE s uredne overenym podpisem. */
const PAPER_ROUTE = 'papir-overeny';

/** Strana, u ktere makler jeste nema cim poslat odkaz (ES2 bod 3). */
function blockedSigner(signers: readonly SignatureSignerDto[]): SignatureSignerDto | null {
  return (
    signers.find(
      (s) =>
        s.status !== 'podepsano' &&
        s.status !== 'zneplatneno' &&
        s.status !== 'zastaveno' &&
        (s.awaitingData || (!s.hasEmail && !s.hasPhone)),
    ) ?? null
  );
}

/** Kolik procent smlouvy je vyplnene; `null` bez osnovy. */
function fillPercent(detail: ContractDetailDto | null | undefined): number | null {
  const plan = detail?.fillPlan;
  if (plan === undefined || plan.total === 0) return null;
  return Math.round(((plan.total - plan.missing) / plan.total) * 100);
}

/**
 * U koho smlouva je, co se o ni ma rict a jedna akce.
 *
 * Poradi vetvi je rozhodnuti, ne nahoda:
 *  1. **Podepsano** vyhrava nad vsim — u hotove smlouvy neni co vytykat.
 *  2. **Papirova cesta** (`papir-overeny`) je vlastni misto: REOS na ni
 *     nemuze cekat ani pripominat, ceka na SKEN s urednim overenim.
 *  3. **Zastavene** podepisovani je na maklerovi (dosavadni podpisy neplati).
 *  4. **Strana bez kontaktu / bez udaju** je na maklerovi, i kdyz se uz
 *     „podepisuje" — pripominka by nemela komu odejit (nalez ES2 bod 3).
 *  5. Teprve pak je smlouva u klientu.
 */
export function contractStanding(input: ContractStandingInput): ContractStanding {
  const { row } = input;
  const session = input.session ?? null;
  const percent = fillPercent(input.detail);
  const signedAt = session?.completedAt ?? null;

  if (row.status === 'signed' || row.status === 'archived' || session?.state === 'podepsano') {
    return {
      where: 'podepsane',
      active: false,
      stateKey: 'webAdmin.contracts.where.stateSigned',
      stateValues: {},
      ...(signedAt !== null
        ? { whyKey: 'webAdmin.contracts.where.whySigned' as MessageKey, whyValues: { date: signedAt } }
        : { whyKey: null, whyValues: {} }),
      action: 'download',
      percent,
    };
  }

  if (session !== null && session.route === PAPER_ROUTE) {
    return {
      where: 'overeni-podpisu',
      active: true,
      stateKey: 'webAdmin.contracts.where.stateNeedScan',
      stateValues: {},
      whyKey: 'webAdmin.contracts.where.whyPaper',
      whyValues: {},
      action: 'upload-scan',
      percent,
    };
  }

  if (session !== null && (session.status === 'zastaveno' || session.status === 'neplatne')) {
    return {
      where: 'na-tobe',
      active: true,
      stateKey: 'webAdmin.contracts.where.stateStopped',
      stateValues: {},
      whyKey: 'webAdmin.contracts.where.whyStopped',
      whyValues: {},
      action: 'continue',
      percent,
    };
  }

  if (session !== null) {
    const blocked = blockedSigner(session.signers);
    if (blocked !== null) {
      return {
        where: 'na-tobe',
        active: true,
        stateKey: 'webAdmin.contracts.where.stateMissingContact',
        stateValues: {},
        whyKey: 'webAdmin.contracts.where.whyMissingContact',
        whyValues: { order: blocked.order + 1 },
        action: 'continue',
        percent,
      };
    }
    return {
      where: 'u-klientu',
      active: true,
      stateKey: 'webAdmin.contracts.where.stateWithClients',
      stateValues: {
        done: session.signers.filter((s) => s.status === 'podepsano').length,
        total: session.signers.length,
      },
      whyKey: 'webAdmin.contracts.where.whySent',
      whyValues: { date: session.createdAt },
      action: 'remind',
      percent,
    };
  }

  // Bez podepisovani je smlouva vzdy na maklerovi: koncept se dopisuje,
  // pripravena se posila. „awaiting_signature" bez sezeni je starsi smlouva
  // z doby pred ES1 — i ta je na maklerovi, protoze odkaz nikdo nevydal.
  if (row.status === 'draft') {
    const missing = input.detail?.fillPlan.missing ?? null;
    return {
      where: 'na-tobe',
      active: true,
      ...(missing !== null && missing > 0
        ? {
            stateKey: 'webAdmin.contracts.where.stateDraft' as MessageKey,
            stateValues: { missing },
          }
        : { stateKey: 'webAdmin.contracts.where.stateDraftReady' as MessageKey, stateValues: {} }),
      whyKey: 'webAdmin.contracts.where.whyDraft',
      whyValues: {},
      action: 'continue',
      percent,
    };
  }
  return {
    where: 'na-tobe',
    active: true,
    stateKey: 'webAdmin.contracts.where.stateReadyToSend',
    stateValues: {},
    whyKey: 'webAdmin.contracts.where.whyReadyToSend',
    whyValues: {},
    action: 'sign',
    percent,
  };
}

/**
 * Posledni podepisovani KAZDE smlouvy z jednoho seznamu sezeni.
 * Server vraci sezeni sestupne (viz `getSession` v `signature-panel.tsx`),
 * takze prvni nalezene je to aktualni.
 */
export function sessionsByContract(
  sessions: readonly SignatureSessionDto[],
): ReadonlyMap<string, SignatureSessionDto> {
  const out = new Map<string, SignatureSessionDto>();
  for (const session of sessions) {
    if (!out.has(session.contractId)) out.set(session.contractId, session);
  }
  return out;
}

/** Stav kazde smlouvy ze stazenych dat — JEDEN vstupni bod pro vsechna mista. */
export function contractStandings(input: {
  readonly rows: readonly ContractDto[];
  readonly sessions?: readonly SignatureSessionDto[] | undefined;
  readonly details?: ReadonlyMap<string, ContractDetailDto> | undefined;
}): ReadonlyMap<string, ContractStanding> {
  const bySession = sessionsByContract(input.sessions ?? []);
  const out = new Map<string, ContractStanding>();
  for (const row of input.rows) {
    out.set(
      row.id,
      contractStanding({
        row,
        session: bySession.get(row.id) ?? null,
        detail: input.details?.get(row.id) ?? null,
      }),
    );
  }
  return out;
}

export function matchesContractFilter(
  standing: ContractStanding,
  filter: ContractWhereFilter,
): boolean {
  if (filter === 'aktivni') return standing.active;
  return standing.where === filter;
}

export function contractFilterCounts(
  standings: Iterable<ContractStanding>,
): Readonly<Record<ContractWhereFilter, number>> {
  const out: Record<ContractWhereFilter, number> = {
    aktivni: 0,
    'na-tobe': 0,
    'u-klientu': 0,
    'overeni-podpisu': 0,
    podepsane: 0,
  };
  for (const standing of standings) {
    for (const filter of CONTRACT_WHERE_FILTERS) {
      if (matchesContractFilter(standing, filter)) out[filter] += 1;
    }
  }
  return out;
}

/**
 * Pocet smluv „Na tobě" — odznak „Smlouvy" v menu (krok SML1 bod 1:
 * „Odznak »Smlouvy« v menu = počet »Na tobě« z téže funkce").
 */
export function onYouContractCount(standings: Iterable<ContractStanding>): number {
  let count = 0;
  for (const standing of standings) if (standing.where === 'na-tobe') count += 1;
  return count;
}

/**
 * Krok MRGP1 — POCTY, JEN KDYZ JSOU ZNAME (`null`, dokud data nedojdou).
 *
 * Pilulky prehledu `/smlouvy` a odznak „Smlouvy" v nabidce profilu ukazuji
 * TOTEZ cislo, ale kazde misto si do tohoto kroku samo rozhodovalo, kdy uz
 * ho zna: prehled kreslil `0` hned pri prvnim vykresleni, menu cekalo na
 * dotaz. 5. 10. 2026 na tom spadla sada S16 na 390 px — odznak 11 proti
 * pilulce 0 (desktop zeleny, protoze data dosla driv nez mereni).
 *
 * „Zname" znamena OBOJE: radky smluv i SEZENI PODPISU. Bez sezeni by
 * smlouva u klienta spadla do „na vás" (test `contract-standing.spec.ts`),
 * takze samotne radky k pravdive odpovedi nestaci.
 */
export function contractCountsIfKnown(input: {
  readonly rows: readonly ContractDto[] | null | undefined;
  readonly sessions: readonly SignatureSessionDto[] | null | undefined;
  readonly details?: ReadonlyMap<string, ContractDetailDto> | undefined;
}): Readonly<Record<ContractWhereFilter, number>> | null {
  const { rows, sessions } = input;
  if (rows === null || rows === undefined || sessions === null || sessions === undefined) return null;
  return contractFilterCounts(
    contractStandings({ rows, sessions, ...(input.details !== undefined ? { details: input.details } : {}) }).values(),
  );
}

/** Odznak „Smlouvy" = pilulka „Na vás" z TEHOZ cisla (`null`, dokud neni zname). */
export function onYouContractCountIfKnown(input: {
  readonly rows: readonly ContractDto[] | null | undefined;
  readonly sessions: readonly SignatureSessionDto[] | null | undefined;
}): number | null {
  return contractCountsIfKnown(input)?.['na-tobe'] ?? null;
}

// ------------------------------------------------- nemovitost v radku prehledu

/**
 * Druhy radek nazvu smlouvy — CO se podepisuje (predloha: „Milovice – Mladá,
 * byt 2+1").
 *
 * Doplneni zadani 4. 10.: smlouva BEZ ZAKAZKY nesmi mit prazdny radek ani
 * „—". Adresa se proto bere z poli smlouvy (`nemovitost.adresa`, jinak ulice
 * a obec) a kdyz ani ta neni vyplnena, radek to REKNE vetou. Prazdne misto
 * vypada jako vada aplikace; „nemovitost nevyplněna" je informace.
 */
export function contractPropertyLine(
  row: Pick<ContractDto, 'caseId' | 'fieldValues'>,
  caseName: string | null,
): { readonly text: string | null; readonly missing: boolean } {
  const value = (key: string): string => {
    const raw = row.fieldValues[key]?.value;
    return typeof raw === 'string' ? raw.trim() : '';
  };
  // Nejdriv ADRESA ZE SMLOUVY: je to presne to, co se podepisuje. Nazev
  // zakazky je az zaloha — na preview to byva „Nemovitost <id>", tedy
  // identita zaznamu, ne misto (nalez ze ziveho overeni).
  const address = value('nemovitost.adresa');
  if (address !== '') return { text: address, missing: false };
  const composed = [value('nemovitost.ulice'), value('nemovitost.obec')]
    .filter((part) => part !== '')
    .join(', ');
  if (composed !== '') return { text: composed, missing: false };
  if (row.caseId !== null && caseName !== null && caseName.trim() !== '') {
    return { text: caseName, missing: false };
  }
  return { text: null, missing: true };
}

// ----------------------------------------- vlastni nazev a hledani v prehledu

/** Text pro srovnavani bez ohledu na velikost pismen, diakritiku a interpunkci. */
function normalizeForSearch(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/**
 * NAZEV SMLOUVY V RADKU PREHLEDU (krok TOKYFIX1, prekazka P2/6).
 *
 * Krok UXF2 bod 2 (nalez N3) nazev z radku vyhodil, protoze DLOUHY PRAVNI
 * NAZEV („Nájemní smlouva k bytu 2+kk, Vichrova 1914/3, Praha 9 — Letňany")
 * radek rozlamoval na dva a na 1440 px se pak vesly ctyri smlouvy jen
 * s posouvanim. Mereni TOKY1 (14. 10. 2026, P2 krok 6) ukazalo druhou stranu
 * teze mince: smlouvu, kterou makleřka pojmenovala pred minutou, v prehledu
 * nepoznala — vsechny radky tehoz typu se jmenuji stejne („Nájem bytu").
 *
 * Obe veci drzi JEDNO reseni: v radku stoji jen KRATKY vlastni nazev, ktery
 * se da precist cely („TOKY Smlouva k zakázce 20261014…", 37 znaku) — stoji
 * na jedinem radku (`.rh-v4-crow__own`), takze vyska radku se nemeni.
 * DLOUHY PRAVNI NAZEV („Nájemní smlouva k bytu 2+kk, Vichrova 1914/3,
 * Praha 9 — Letňany", 63 znaku) zustava podle UXF2 az v detailu: v radku by
 * se stejne orizl tremi teckami, takze by smlouvu rozpoznat nepomohl, jen by
 * zabral misto. Adresa ma v radku svuj vlastni text (`contracts-property-*`).
 *
 * `null` = neukazovat (prazdny nazev, tentyz co typ smlouvy, nebo delsi nez
 * `OWN_TITLE_MAX`).
 */
export const OWN_TITLE_MAX = 48;

export function contractOwnTitle(input: {
  readonly title: string;
  readonly typeLabel: string;
}): string | null {
  const title = input.title.trim();
  if (title === '') return null;
  if (title.length > OWN_TITLE_MAX) return null;
  if (normalizeForSearch(title) === normalizeForSearch(input.typeLabel)) return null;
  return title;
}

/**
 * HLEDANI V PREHLEDU SMLUV (krok TOKYFIX1, prekazka P2/6) — nazev, klient,
 * adresa. Jedna cista funkce, aby „Hledat" znamenalo totez ve prehledu
 * i v zalozce zakazky.
 */
export function matchesContractQuery(
  haystack: {
    readonly title: string;
    readonly typeLabel: string;
    readonly propertyText: string | null;
    readonly partyNames: readonly string[];
  },
  query: string,
): boolean {
  const needle = normalizeForSearch(query);
  if (needle === '') return true;
  const text = normalizeForSearch(
    [haystack.title, haystack.typeLabel, haystack.propertyText ?? '', ...haystack.partyNames].join(' '),
  );
  return needle.split(' ').every((word) => text.includes(word));
}

// --------------------------------------------------- stav strany v radku prehledu

export type PartyChipTone = 'done' | 'progress' | 'waiting';

export interface PartyChip {
  readonly id: string;
  /** Jmeno role strany — sklada ho volajici z katalogu, ne tahle funkce. */
  readonly role: string;
  readonly stateKey: MessageKey;
  readonly tone: PartyChipTone;
}

/** Stav podepisujici strany podle SKUTECNEHO sezeni (ne podle stavu smlouvy). */
function signerTone(signer: SignatureSignerDto): PartyChipTone {
  if (signer.status === 'podepsano') return 'done';
  if (signer.status === 'otevreno' || signer.status === 'souhlaseno') return 'progress';
  if (signer.status === 'odeslano' || signer.status === 'k-odeslani') return 'progress';
  return 'waiting';
}

const SIGNER_CHIP_KEY: Readonly<Record<PartyChipTone, MessageKey>> = {
  done: 'webAdmin.contracts.where.chipSigned',
  progress: 'webAdmin.contracts.where.chipSigning',
  waiting: 'webAdmin.contracts.where.chipWaiting',
};

const INVITE_CHIP_KEY: Readonly<Record<PartyChipTone, MessageKey>> = {
  done: 'webAdmin.contracts.where.chipFilled',
  progress: 'webAdmin.contracts.where.chipFilling',
  waiting: 'webAdmin.contracts.where.chipNoData',
};

/**
 * Stitky stran v radku prehledu (predloha: „✓ nájemce", „nájemce vyplňuje",
 * „pronajímatel neotevřel").
 *
 * Zdroj je bud SEZENI (kdyz se uz podepisuje — tam je stav podpisu), nebo
 * POZVANKY klientu (dokud se vyplnuji udaje). Nikdy stav smlouvy: ten rika,
 * co dela dokument, ne co dela clovek.
 */
export function partyChips(input: {
  readonly session?: SignatureSessionDto | null | undefined;
  readonly parties: readonly { readonly role: string; readonly index: number; readonly filled: boolean }[];
  readonly invitations?: readonly PartyInvitationRef[] | undefined;
  readonly roleName: (role: string, index: number) => string;
}): readonly PartyChip[] {
  const session = input.session ?? null;
  if (session !== null && session.signers.length > 0) {
    return session.signers.map((signer) => {
      const tone = signerTone(signer);
      return {
        id: signer.id,
        role: input.roleName(signer.partyKey, signer.partyIndex),
        stateKey: SIGNER_CHIP_KEY[tone],
        tone,
      };
    });
  }
  const invitations = input.invitations ?? [];
  return input.parties.map((party) => {
    const invitation = invitations.find(
      (i) => i.partyKey === party.role && i.partyIndex === party.index,
    );
    const tone: PartyChipTone =
      invitation?.submittedAt != null || invitation?.status === 'zkontrolovano-maklerem'
        ? 'done'
        : party.filled
          ? 'done'
          : invitation?.openedAt != null || invitation?.status === 'rozpracovano'
            ? 'progress'
            : invitation !== undefined
              ? 'progress'
              : 'waiting';
    return {
      id: `${party.role}-${String(party.index)}`,
      role: input.roleName(party.role, party.index),
      stateKey: INVITE_CHIP_KEY[tone],
      tone,
    };
  });
}
